import React, { useState, useEffect } from "react";
import { Button, Select, Input, Icon, Form, FormField } from "semantic-ui-react";
import { toast } from "react-toastify";
import { Inspection, User, Site, Area } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useCompany, useSite } from "../../../../contexts";
import { useTemplateCatalog, TemplateCard } from "../ChecklistTemplateCatalog";
import { useChecklistSiteIndustry } from "../useChecklistSiteIndustry";
import "./ChecklistForm.scss";

const inspCtrl = new Inspection();
const userCtrl = new User();
const siteCtrl = new Site();
const areaCtrl = new Area();

// ── Paso 1: Selección de plantilla ───────────────────────────────────────────

function StepSelectTemplate({ onNext, initialTemplate, siteIndustry }) {
  const { templates, industryOptions, normRefs, search, setSearch, filterInd, setFilterInd, loading } =
    useTemplateCatalog(siteIndustry);
  const [selected, setSelected] = useState(initialTemplate || null);

  return (
    <div className="clf__step">
      <div className="clf__step-lead">
        <div className="clf__step-dot active">1</div>
        <div>
          <div className="clf__step-title">Selección de checklist</div>
          <div className="clf__step-sub">Elegí la plantilla de inspección a ejecutar</div>
        </div>
      </div>

      <div className="ctpl__filters">
        <Input
          icon="search"
          iconPosition="left"
          placeholder="Buscar por nombre o industria..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="ctpl__search"
          loading={loading}
        />
        <Select
          options={industryOptions}
          value={filterInd}
          onChange={(_, d) => setFilterInd(d.value)}
          placeholder="Industria"
          className="ctpl__ind-select"
          clearable
        />
      </div>

      {loading ? (
        <div className="ctpl__loading">Cargando plantillas...</div>
      ) : templates.length === 0 ? (
        <div className="ctpl__empty">
          <Icon name="clipboard outline" size="large" />
          <p>No se encontraron plantillas{search || filterInd ? " con esos filtros" : ""}.</p>
        </div>
      ) : (
        <div className="ctpl__grid">
          {templates.map((t) => (
            <TemplateCard
              key={t._id}
              tpl={t}
              selected={selected?._id === t._id}
              onClick={setSelected}
              normRefs={normRefs}
            />
          ))}
        </div>
      )}

      <div className="clf__step-footer">
        <span className="clf__footer-hint">
          {selected ? `Seleccionado: ${selected.name}` : "Seleccioná una plantilla para continuar"}
        </span>
        <Button primary disabled={!selected} onClick={() => onNext(selected)}>
          Continuar <Icon name="arrow right" />
        </Button>
      </div>
    </div>
  );
}

// ── Paso 2: Datos de la inspección ───────────────────────────────────────────

function StepHeaderData({ template, onBack, onStart }) {
  const { accessToken, user } = useAuth();
  const { company }           = useCompany();
  const { site: contextSite } = useSite();

  const [users,  setUsers]  = useState([]);
  const [sites,  setSites]  = useState([]);
  const [areas,  setAreas]  = useState([]);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    inspector: user?._id || "",
    site:      contextSite || "",
    area:      "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [u, s, a] = await Promise.all([
          userCtrl.getUsers(accessToken, true),
          company
            ? siteCtrl.getSitesByCompany(accessToken, company, true)
            : siteCtrl.getSites(accessToken, true),
          areaCtrl.getAreas(accessToken, true),
        ]);
        setUsers(Array.isArray(u) ? u : []);
        setSites(Array.isArray(s) ? s : []);
        setAreas(Array.isArray(a) ? a : []);
      } catch {
        setUsers([]); setSites([]); setAreas([]);
      }
    })();
  }, [accessToken, company]);

  const set = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => { const e = { ...prev }; delete e[field]; return e; });
  };

  const validate = () => {
    const e = {};
    if (!form.inspector) e.inspector = "Requerido";
    if (!form.site)      e.site      = "Requerido";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleStart = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        template:  template._id,
        inspector: form.inspector,
        site:      form.site,
        area:      form.area || undefined,
        // Fecha real y GPS los completa el inspector desde su celular al
        // ejecutar la inspección (ver ChecklistExecution.js). Acá solo se
        // guarda un valor inicial porque el modelo lo requiere.
        date:      new Date().toISOString(),
        status:    "draft",
      };
      const res = await inspCtrl.createInspection(accessToken, payload);
      onStart({ inspection: res.inspection || res, template });
    } catch (err) {
      toast.error(err?.msg || "Error al iniciar la inspección.");
    } finally {
      setSaving(false);
    }
  };

  const userOptions = users.map((u) => ({
    key: u._id, value: u._id,
    text: [u.name, u.lastname].filter(Boolean).join(" ") || u.email,
  }));
  const siteOptions = sites.map((s) => ({ key: s._id, value: s._id, text: s.name }));
  const areaOptions = [
    { key: "none", value: "", text: "Sin área específica" },
    ...areas.map((a) => ({ key: a._id, value: a._id, text: a.name })),
  ];

  return (
    <div className="clf__step">
      <div className="clf__step-lead">
        <div className="clf__step-dot active">2</div>
        <div>
          <div className="clf__step-title">Datos de la inspección</div>
          <div className="clf__step-sub">
            <strong>{template.name}</strong>
            {template.industry?.name && ` · ${template.industry.name}`}
            {` · ${template.items?.length ?? 0} ítems`}
          </div>
        </div>
      </div>

      <Form className="clf__form">
        <FormField error={!!errors.inspector}>
          <label>Inspector / Responsable *</label>
          <Select
            options={userOptions}
            value={form.inspector}
            onChange={(_, d) => set("inspector", d.value)}
            search
            placeholder="Seleccionar inspector"
          />
          {errors.inspector && <span className="clf__error">{errors.inspector}</span>}
        </FormField>

        <FormField error={!!errors.site}>
          <label>Sitio / Planta *</label>
          <Select
            options={siteOptions}
            value={form.site}
            onChange={(_, d) => set("site", d.value)}
            search
            placeholder="Seleccionar sitio"
          />
          {errors.site && <span className="clf__error">{errors.site}</span>}
        </FormField>

        <FormField>
          <label>Sector / Área inspeccionada</label>
          <Select
            options={areaOptions}
            value={form.area}
            onChange={(_, d) => set("area", d.value)}
            search
            placeholder="(Opcional)"
            clearable
          />
        </FormField>

        <div className="clf__step-hint">
          <Icon name="mobile alternate" />
          La fecha real y la geolocalización las registra el inspector desde
          su celular al completar la inspección.
        </div>
      </Form>

      <div className="clf__step-footer">
        <Button basic type="button" onClick={onBack}>
          <Icon name="arrow left" /> Volver
        </Button>
        <Button primary onClick={handleStart} loading={saving} disabled={saving}>
          <Icon name="play circle" /> Iniciar inspección
        </Button>
      </div>
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────

export function ChecklistForm({ close, onStart, initialTemplate }) {
  const [step,     setStep]     = useState(initialTemplate ? 2 : 1);
  const [template, setTemplate] = useState(initialTemplate || null);
  const { site: activeSite } = useSite();
  const [siteIndustry] = useChecklistSiteIndustry(activeSite);

  const handleTemplateNext = (tpl) => { setTemplate(tpl); setStep(2); };

  return (
    <div className="clf">
      {/* Cabecera degradada (igual que WorkPermitForm) */}
      <div className="clf-drawer-header">
        <div className="clf-drawer-header__left">
          <span className="clf-drawer-header__id-pill">
            INSP-{new Date().getFullYear()}
          </span>
          <div>
            <div className="clf-drawer-header__title">Nueva inspección</div>
            <div className="clf-drawer-header__subtitle">
              Seleccioná un checklist y completá los datos de la recorrida
            </div>
          </div>
        </div>

        {/* Stepper compacto */}
        <div className="clf-drawer-header__stepper">
          {[1, 2, 3].map((n) => (
            <React.Fragment key={n}>
              <span className={`clf-drawer-header__dot${step >= n ? " active" : ""}`}>{n}</span>
              {n < 3 && <span className="clf-drawer-header__line" />}
            </React.Fragment>
          ))}
        </div>

        <button className="clf-drawer-header__close-btn" onClick={close} type="button">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Contenido scrollable */}
      <div className="clf__scroll">
        {step === 1 && (
          <StepSelectTemplate onNext={handleTemplateNext} initialTemplate={template} siteIndustry={siteIndustry} />
        )}
        {step === 2 && (
          <StepHeaderData
            template={template}
            onBack={() => setStep(1)}
            onStart={onStart}
          />
        )}
      </div>
    </div>
  );
}
